import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Shield, Users, FileText, Bell, Database, Settings, Lock,
  Eye, Edit, Trash2, Plus, Check, X, AlertTriangle, UserCog
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Permission definitions with icons and descriptions
const PERMISSION_INFO = {
  consent: { icon: FileText, label: 'Consentimentos', color: 'bg-blue-100 text-blue-700' },
  titular: { icon: Users, label: 'Titulares', color: 'bg-green-100 text-green-700' },
  rtbf: { icon: Trash2, label: 'RTBF', color: 'bg-red-100 text-red-700' },
  marketing: { icon: Bell, label: 'Marketing', color: 'bg-purple-100 text-purple-700' },
  group: { icon: Users, label: 'Grupos', color: 'bg-indigo-100 text-indigo-700' },
  analytics: { icon: Database, label: 'Analytics', color: 'bg-cyan-100 text-cyan-700' },
  ropa: { icon: FileText, label: 'ROPA', color: 'bg-amber-100 text-amber-700' },
  breach: { icon: AlertTriangle, label: 'Violações', color: 'bg-red-100 text-red-700' },
  user: { icon: UserCog, label: 'Utilizadores', color: 'bg-slate-100 text-slate-700' },
  tenant: { icon: Database, label: 'Tenants', color: 'bg-emerald-100 text-emerald-700' },
  audit: { icon: Eye, label: 'Auditoria', color: 'bg-gray-100 text-gray-700' },
  backup: { icon: Database, label: 'Backups', color: 'bg-orange-100 text-orange-700' },
  settings: { icon: Settings, label: 'Definições', color: 'bg-pink-100 text-pink-700' },
  notification: { icon: Bell, label: 'Notificações', color: 'bg-yellow-100 text-yellow-700' },
};

const ACTION_ICONS = {
  view: Eye,
  create: Plus,
  update: Edit,
  delete: Trash2,
  export: FileText,
  approve: Check,
  execute: Settings,
  notify: Bell,
  restore: Database,
  broadcast: Bell,
};

const ROLE_INFO = {
  super_admin: { label: 'Super Admin', description: 'Acesso total a todos os tenants', color: 'bg-red-500' },
  tenant_admin: { label: 'Admin Tenant', description: 'Administrador da organização', color: 'bg-orange-500' },
  admin: { label: 'Administrador', description: 'Gestão operacional completa', color: 'bg-blue-500' },
  dpo: { label: 'DPO', description: 'Data Protection Officer - Conformidade', color: 'bg-purple-500' },
  operador: { label: 'Operador', description: 'Operações de marketing', color: 'bg-green-500' },
  titular: { label: 'Titular', description: 'Titular de dados pessoais', color: 'bg-slate-500' },
};

const PermissionsPage = () => {
  const { user } = useOutletContext();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(user?.role || 'admin');

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me/permissions`, { withCredentials: true });
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Erro ao carregar permissões');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const IconComponent = ACTION_ICONS[action] || Eye;
    return <IconComponent className="w-3 h-3" />;
  };

  const getActionLabel = (action) => {
    const labels = {
      view: 'Ver',
      create: 'Criar',
      update: 'Editar',
      delete: 'Eliminar',
      export: 'Exportar',
      approve: 'Aprovar',
      execute: 'Executar',
      notify: 'Notificar',
      restore: 'Restaurar',
      broadcast: 'Difundir',
    };
    return labels[action] || action;
  };

  if (!user || !['admin', 'dpo', 'super_admin', 'tenant_admin'].includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <p className="text-red-500 font-medium">Acesso Restrito</p>
          <p className="text-slate-500 text-sm">Apenas administradores podem ver esta página</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permissions-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Gestão de Permissões
        </h1>
        <p className="text-slate-600">
          Sistema de controlo de acessos baseado em roles (RBAC)
        </p>
      </div>

      {/* Current User Permissions */}
      <Card className="border-l-4 border-l-[#10B981]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#10B981]" />
            As Suas Permissões
          </CardTitle>
          <CardDescription>
            Role atual: <Badge className={ROLE_INFO[user.role]?.color}>{ROLE_INFO[user.role]?.label}</Badge>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {permissions && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-[#10B981]">{permissions.total_permissions}</p>
                <p className="text-sm text-slate-500">Total Permissões</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{Object.keys(permissions.categories || {}).length}</p>
                <p className="text-sm text-slate-500">Categorias</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">
                  {permissions.permissions?.filter(p => p.includes(':view')).length || 0}
                </p>
                <p className="text-sm text-slate-500">Visualização</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-amber-600">
                  {permissions.permissions?.filter(p => p.includes(':create') || p.includes(':update')).length || 0}
                </p>
                <p className="text-sm text-slate-500">Edição</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions by Category */}
      <Card>
        <CardHeader>
          <CardTitle>Permissões por Categoria</CardTitle>
          <CardDescription>Detalhes das suas permissões agrupadas por funcionalidade</CardDescription>
        </CardHeader>
        <CardContent>
          {permissions?.categories && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(permissions.categories).map(([category, perms]) => {
                const info = PERMISSION_INFO[category] || { 
                  icon: Lock, 
                  label: category.charAt(0).toUpperCase() + category.slice(1),
                  color: 'bg-gray-100 text-gray-700'
                };
                const IconComponent = info.icon;
                
                return (
                  <div key={category} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-lg ${info.color}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <h4 className="font-medium">{info.label}</h4>
                      <Badge variant="outline" className="ml-auto">{perms.length}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {perms.map((perm) => {
                        const action = perm.split(':')[1];
                        return (
                          <Badge 
                            key={perm} 
                            variant="secondary" 
                            className="text-xs flex items-center gap-1"
                          >
                            {getActionIcon(action)}
                            {getActionLabel(action)}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Comparação de Roles</CardTitle>
          <CardDescription>Visão geral das permissões por role</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedRole} onValueChange={setSelectedRole}>
            <TabsList className="mb-4 flex-wrap h-auto gap-2">
              {Object.entries(ROLE_INFO).map(([role, info]) => (
                <TabsTrigger 
                  key={role} 
                  value={role}
                  className="flex items-center gap-2"
                >
                  <div className={`w-2 h-2 rounded-full ${info.color}`} />
                  {info.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(ROLE_INFO).map(([role, info]) => (
              <TabsContent key={role} value={role}>
                <div className="p-4 bg-slate-50 rounded-lg mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-4 h-4 rounded-full ${info.color}`} />
                    <h4 className="font-semibold text-lg">{info.label}</h4>
                  </div>
                  <p className="text-slate-600">{info.description}</p>
                </div>

                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Ver</TableHead>
                        <TableHead>Criar</TableHead>
                        <TableHead>Editar</TableHead>
                        <TableHead>Eliminar</TableHead>
                        <TableHead>Especiais</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(PERMISSION_INFO).map(([category, catInfo]) => {
                        const hasView = role === 'super_admin' || (category !== 'tenant' || ['super_admin', 'tenant_admin'].includes(role));
                        const hasCreate = ['super_admin', 'tenant_admin', 'admin'].includes(role) || (role === 'operador' && ['consent', 'titular', 'group', 'marketing'].includes(category));
                        const hasEdit = hasCreate;
                        const hasDelete = ['super_admin', 'tenant_admin', 'admin'].includes(role);
                        const hasSpecial = role === 'dpo' && ['rtbf', 'marketing', 'breach'].includes(category);
                        
                        return (
                          <TableRow key={category}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <catInfo.icon className="w-4 h-4 text-slate-500" />
                                {catInfo.label}
                              </div>
                            </TableCell>
                            <TableCell>
                              {hasView ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <X className="w-4 h-4 text-red-300" />
                              )}
                            </TableCell>
                            <TableCell>
                              {hasCreate ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <X className="w-4 h-4 text-red-300" />
                              )}
                            </TableCell>
                            <TableCell>
                              {hasEdit ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <X className="w-4 h-4 text-red-300" />
                              )}
                            </TableCell>
                            <TableCell>
                              {hasDelete ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <X className="w-4 h-4 text-red-300" />
                              )}
                            </TableCell>
                            <TableCell>
                              {hasSpecial && (
                                <Badge variant="outline" className="text-xs">
                                  Aprovar
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default PermissionsPage;
